import React, {
 useEffect, useMemo, useRef, useState,
} from 'react';
import { useInView } from 'framer-motion';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';

import Location from '@r6digital/rapidstor-types/react/entities/Location';

import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
    fas,
    faBox,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { descriptorDimensions, showPricesButtonText } from '@/core/utils/rapidstor';
import { UnitTypeWithImages } from '@/standard-unit-listing/screens';

import store from '@/store';
import { setSizesViewed, setDeal } from '@/store/bookingSlice';

import Button from '@/standard-unit-listing/components/elements/button';

import {
    getFloorString,
    unitTypeTitle,
    sortUnitTypes,
} from '@/features/rapidstor/helpers';

import classes from './styles.module.scss';

import UnitTypeMeta, { useSizeCategoryKey } from '../shared/UnitTypeMeta';
import UnitTypeImageGallery from './ImageGallery';
import DealPriceCards from './DealPriceCards';
import CTAGroup from './CTAGroup';

const solidIconsByName: Record<string, IconDefinition> = Object.fromEntries(
    Object.values(fas).map((icon) => [icon.iconName, icon]),
);
// "image-user" was the old fixed picker's id for this icon - not a real FontAwesome name.
solidIconsByName['image-user'] = solidIconsByName.user;

export interface UnitTypeCardProps {
    // For backwards compatibility supply a single unitType
    unitType?: UnitTypeWithImages;
    // When grouping by typeName, multiple variants (e.g., floors) of the same size/type
    unitTypes?: UnitTypeWithImages[];
    location: Location;
    onBookOnline?: (unitType: UnitTypeWithImages) => void;
    onSendEnquiry?: (unitType: UnitTypeWithImages) => void;
    onShowPrices?: (unitType: UnitTypeWithImages) => void;
}

export default function UnitTypeCard({
    unitType: singleUnitType,
    unitTypes,
    location,
    onBookOnline,
    onSendEnquiry,
    onShowPrices,
}: UnitTypeCardProps) {
    const { t } = useTranslation();

    const variants = useMemo<UnitTypeWithImages[]>(() => {
        let list: UnitTypeWithImages[] = [];
        if (unitTypes && unitTypes.length > 0) list = unitTypes;
        else if (singleUnitType) list = [singleUnitType];
        if (list.length === 0) return list;

        return sortUnitTypes(list, location) as UnitTypeWithImages[];
    }, [unitTypes, singleUnitType, location]);
    const [activeIndex, setActiveIndex] = useState(0);
    const unitType = variants[activeIndex];
    // Selected deal for the active variant (null = standard price). Defaults to
    // the first/promoted deal, matching the reservation flow's default.
    const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
    const [cookies] = useCookies(['contactData']);
    const ref = useRef<HTMLDivElement>(null);
    const isInView = useInView(ref, { once: true });

    const sizeCategory = useSizeCategoryKey(unitType, location);
    const sizeBadgeLabel = useMemo<string | null>(() => {
        switch (sizeCategory) {
            case 'small':
                return t('standardUnitListing:unitType:smallUnit', 'Small Unit');
            case 'medium':
                return t('standardUnitListing:unitType:mediumUnit', 'Medium Unit');
            case 'large':
                return t('standardUnitListing:unitType:largeUnit', 'Large Unit');
            default:
                return null;
        }
    }, [sizeCategory, t]);

    useEffect(() => {
        if (isInView) {
            store.dispatch(
                setSizesViewed(descriptorDimensions(unitType, location, { spaceAroundSeparator: false })),
            );
        }
    }, [isInView, unitType, location]);

    // Reset the selected deal to the promoted one whenever the active variant
    // changes, since deals differ per variant (floor/level).
    useEffect(() => {
        setSelectedDealId(unitType?.deals?.[0]?._id ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unitType?._id]);

    // Push the chosen deal into the reservation store so booking / enquiry flows
    // use it, then delegate to the parent handler.
    const withSelectedDeal = (
        handler?: (ut: UnitTypeWithImages) => void,
    ) => (ut: UnitTypeWithImages) => {
        const deal = ut.deals?.find((d) => d._id === selectedDealId);
        store.dispatch(
            setDeal({ deal: selectedDealId, dealObject: deal ?? undefined }),
        );
        handler?.(ut);
    };

    if (!unitType) return null;

    return (
        <section
            className={classes.card}
            role="complementary"
            aria-label="UnitType pricing and actions"
            ref={ref}
        >
            <UnitTypeImageGallery unitType={unitType} />
            <div className={classes.unitTypeDetails}>
                <div className={classes.titleRow}>
                    <span className={classes.title}>
                        {unitTypeTitle(unitType, location)}
                    </span>
                    {sizeBadgeLabel ? (
                        <span className={classes.sizeBadge}>{sizeBadgeLabel}</span>
                    ) : null}
                </div>

                {variants.length > 1 ? (
                    <fieldset className={classes.variantSelector}>
                        <div className={classes.variantOptions}>
                            {variants.map((v, idx) => (
                                <label
                                    key={v._id}
                                    className={classes.variantOption}
                                >
                                    <input
                                        type="radio"
                                        name={`variant-${variants[0]._id}`}
                                        value={idx}
                                        checked={activeIndex === idx}
                                        onChange={() => setActiveIndex(idx)}
                                    />
                                    <span>
                                        {t(
                                            'standardUnitListing:unitType:level',
                                            'Level',
                                        )}
                                        :
                                        {' '}
                                        {getFloorString(v, location)}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </fieldset>
                ) : null}

                <UnitTypeMeta
                    unitType={unitType}
                    location={location}
                    classes={classes}
                />

                <div className={classes.description}>
                    <span className={classes.header}>
                        {t('standardUnitListing:unitType:whatCanItFit')}
                    </span>
                    <span className={classes.body}>{unitType.description}</span>
                </div>

                {unitType.tags.some((tag) => tag.enabled) ? (
                    <div className={classes.tags}>
                        {unitType.tags
                            .filter((tag) => tag.enabled)
                            .slice(0, 4)
                            .map((tag, idx) => (
                                <span
                                    key={`${tag.icon}_${idx}`}
                                    className={classes.tag}
                                >
                                    <span className={classes.iconWrap}>
                                        <FontAwesomeIcon
                                            width={20}
                                            icon={solidIconsByName[tag.icon] || faBox}
                                            className={classes.icon}
                                        />
                                    </span>
                                    <span className={classes.content}>
                                        {tag.content}
                                    </span>
                                </span>
                            ))}
                    </div>
                ) : null}
            </div>
            <div className={classes.pricingContainer}>
                {location.rapidstor.hidePrices && !cookies.contactData ? (
                    <Button
                        type="primary"
                        size="medium"
                        style={{
                            minWidth: '180px',
                        }}
                        onclick={() => onShowPrices && onShowPrices(unitType)}
                    >
                        {showPricesButtonText(location)}
                    </Button>
                ) : (
                    <>
                        <DealPriceCards
                            unitType={unitType}
                            location={location}
                            selectedDealId={selectedDealId}
                            onSelectDeal={setSelectedDealId}
                        />
                        <CTAGroup
                            location={location}
                            unitType={unitType}
                            onBookOnline={withSelectedDeal(onBookOnline)}
                            onSendEnquiry={withSelectedDeal(onSendEnquiry)}
                        />
                    </>
                )}
            </div>
        </section>
    );
}
